import { KeyboardCode } from "@dnd-kit/core";
import { Modal, ModalProps } from "antd";
import { MaskType } from "antd/es/_util/hooks";
import { FC, KeyboardEvent, UIEvent, useCallback, useEffect, useRef } from "react";
import "./Modal.less";

interface XmModalProps extends ModalProps {
  onOk?: (e: UIEvent<HTMLElement>) => void;
}

interface IXmModal extends FC<XmModalProps> {
  MASK_NOT_CLOSABLE: Readonly<MaskType>;
}

const XmModalFn: FC<XmModalProps> = props => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.open) {
      // Delay focus to allow the modal to render
      const timer = setTimeout(() => {
        const wrapper = wrapperRef.current;
        // Keep focus already inside the modal, so autoFocus keeps working (same guard as rc-dialog)
        if (wrapper && !wrapper.contains(document.activeElement))
          wrapper.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [props.open]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    const isDropdownOpen = !!(
      document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)') ||
      document.querySelector('.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)')
    );

    // Ignore the ESC key if the dropdown is open
    if (e.key === 'Escape' && isDropdownOpen) {
      e.stopPropagation();
      return;
    }

    // If a nested modal is open on top, delegate Enter to its OK button
    const allModals = document.querySelectorAll('.ant-modal-wrap:not([style*="display: none"])');
    if (allModals.length > 1 && wrapperRef.current) {
      const thisModal = wrapperRef.current.closest('.ant-modal-wrap');
      const lastModal = allModals[allModals.length - 1];
      if (thisModal && lastModal && thisModal !== lastModal) {
        if (e.key === 'Enter' && !isDropdownOpen) {
          const okButton = lastModal.querySelector<HTMLButtonElement>('.ant-btn-primary');
          okButton?.click();
          setTimeout(() => {
            const wrapper = wrapperRef.current;
            if (wrapper && !wrapper.contains(document.activeElement))
              wrapper.focus();
          }, 300);
        }
        e.stopPropagation();
        return;
      }
    }

    if (e.key !== KeyboardCode.Enter) return;

    const target = e.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();

    // Ignore Enter in dropdown menus, buttons, and form
    if (
      tagName === 'button' ||
      target.closest('.ant-select-dropdown') ||
      target.closest('form') ||
      isDropdownOpen
    ) {
      return;
    }
    if (props?.onOk) {
      props.onOk(e);
    }
  }, [props]);

  return (
    <Modal
      {...props}
      className={`xm-modal ${props.className || ''}`}
      modalRender={(modal) => (
        <div ref={wrapperRef} tabIndex={-1} onKeyDown={handleKeyDown}>
          {modal}
        </div>
      )}
      closable
    />
  );
};

export const XmModal: IXmModal = Object.assign(XmModalFn, {
  MASK_NOT_CLOSABLE: { closable: false },
});
